import {LoaderFunctionArgs} from 'react-router-dom';
import {runInAction} from 'mobx';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {IRouteStore} from '../../router/routing.infra';
import {DashboardState} from '../dashboard/_contracts/DashboardState';
import {ClientRoomStorage} from '../dashboard/_contracts/ClientRoomStorage';
import {DashboardStores, setDashboardStore} from '../dashboard/dashboard.stores';
import {getDashboardLanguage, setDashboardLanguage} from '../helper/general.helper';
import {resolveEmbeddedInfo, resolveDashboardUserAndAccount, resolveRoomData} from '../helper/dashboard-resolve.helper';
import {MultipartyDashboardStores} from './MultipartyDashboardStores';

const trace = getAppTracer('MultipartyDashboardLoader');

export async function multipartyDashboardRouteLoader(args: LoaderFunctionArgs): Promise<IRouteStore> {
    trace.info('Multiparty dashboard loader starting');
    const url = new URL(args.request.url);
    const rootStore = getRootStore();

    await rootStore.initRootServicesCompleted;

    const embeddedInfo = resolveEmbeddedInfo(url);

    const {currentUser, accountData} = await resolveDashboardUserAndAccount(embeddedInfo, url);

    rootStore.tsExtraLoggingService.init(accountData, rootStore.eventLogService);

    const roomData = await resolveRoomData(currentUser, accountData, embeddedInfo, url);

    const accountLanguage = getDashboardLanguage(accountData, roomData);

    setDashboardLanguage(accountLanguage);
    await rootStore.translationHelper.storeAccountCustomStrings(accountData._id);
    await rootStore.tsLocaleHelper.loadLocale(accountLanguage, accountData.companyName);

    const clientRoomStorage = new ClientRoomStorage();
    const dashboardState = new DashboardState(accountData, currentUser, roomData, clientRoomStorage);

    runInAction(() => {
        dashboardState.isObserve = !!url.searchParams.get('observe');
        dashboardState.isOfflineRoom = roomData.offline && url.searchParams.get('offline') !== 'true';
        dashboardState.isOfflineMode = url.searchParams.get('offline') === 'true';
        dashboardState.audioRequestedFromUrl = url.searchParams.get('audio') === 'yes';
        dashboardState.roomCode = url.searchParams.get('roomCode') || '';
        dashboardState.embeddedInfo = embeddedInfo;
    });

    const dashboardStores = new DashboardStores(dashboardState);

    setDashboardStore(dashboardStores);

    await dashboardStores.initStore({skipMediaService: true});

    const mpStores = new MultipartyDashboardStores();

    await mpStores.initStores();

    trace.info('Multiparty dashboard loader completed successfully');

    return mpStores;
}
